'use client';
import { Box, Tooltip } from '@mui/material';
import { FC, PropsWithChildren } from 'react';

import { AbgTypography, formatMoney, mergeSx } from '@abg/kit-base';
import { CrossIcon, InfoIcon, TickIcon } from '@abg/kit-base/lib/icons';

import { useGlobalRegions } from '../../../hooks';
import {
  GlobalRegion,
  ICON_ONLY_BUNDLE_ITEM_CODES,
} from '../../../shared/constants';
import {
  replaceTemplatePlaceholders,
  sanitizeHtml,
} from '../../../shared/utils';

import {
  containerStyling,
  crossIconStyling,
  iconContainerStyling,
  infoIconStyling,
  itemStyling,
  priceStyling,
  tickIconStyling,
  titleStyling,
} from './AncillariesBundleItems.styling';
import {
  AncillariesBundleItemsProps,
  AncillariesBundleStyleParts,
} from './types';

const IconContainer: FC<PropsWithChildren> = ({ children }) => (
  <Box sx={iconContainerStyling}>{children}</Box>
);

const getIconComponent = (
  included: boolean,
  styleParts: AncillariesBundleStyleParts,
) => {
  switch (true) {
    case included: {
      return (
        <TickIcon
          sx={mergeSx(tickIconStyling, styleParts.itemTickIcon)}
          className="bundleItemIcon"
        />
      );
    }
    case !included: {
      return (
        <CrossIcon
          sx={mergeSx(crossIconStyling, styleParts.itemCrossIcon)}
          className="bundleItemIcon"
        />
      );
    }
    default: {
      return null;
    }
  }
};

export const AncillariesBundleItems = ({
  styleParts = {},
  items,
}: AncillariesBundleItemsProps) => {
  const { belongsToRegions } = useGlobalRegions();
  const isEmea = belongsToRegions([GlobalRegion.EMEA]);

  return (
    <Box sx={containerStyling}>
      {items.map((item) => {
        const price = formatMoney(Number(item.price ?? 0), {
          currency: item.currency,
        });

        const description =
          replaceTemplatePlaceholders(item.description, {
            price,
          }) ?? '';

        return (
          <Box
            sx={itemStyling}
            key={item.title}
            data-testid={`ancillaries-bundle-item-${item.included ? 'included' : 'excluded'}`}
          >
            <IconContainer>
              {getIconComponent(item.included, styleParts)}
            </IconContainer>

            <AbgTypography
              variant="bodySmallRegular"
              sx={titleStyling(item.included)}
            >
              {item.title}
            </AbgTypography>

            {isEmea &&
              !ICON_ONLY_BUNDLE_ITEM_CODES.has(item.code) &&
              item.price && (
                <AbgTypography
                  variant="bodySmallRegular"
                  sx={priceStyling(item.price === '0')}
                  data-testid="ancillaries-bundle-item-price"
                >
                  {price}
                </AbgTypography>
              )}

            <IconContainer>
              <Tooltip
                slotProps={{
                  tooltip: { sx: { ...styleParts.itemTooltip } },
                  arrow: { sx: { ...styleParts.itemTooltipArrow } },
                }}
                title={
                  <span
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(description),
                    }}
                  />
                }
                arrow
                enterTouchDelay={1}
              >
                <InfoIcon
                  sx={mergeSx(infoIconStyling, styleParts.itemInfoIcon)}
                  onClick={(event) => event.stopPropagation()}
                />
              </Tooltip>
            </IconContainer>
          </Box>
        );
      })}
    </Box>
  );
};
